import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {buildEntryAndExitForm, getEntryAndExitFormByKey} from "../../Model/Model";
import HandleChange from "../../../../../Helpers/HandleChange";
import {PHOTO_PICKER_MODEL} from "../../../../../components/PhotoPicker/SubComponent/Models/Models";
import routes from "../../../../../Routes/routes";
import {CreateMaterialControl} from "../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import {errorMsg} from "../../../../../Apis/functions";
import {USER_TYPE_NAME_MODEL} from "../../../../../enums/userTypeEnum";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 */
const CreateEntryAndExitFormApp = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    let {state} = location;
    // The form is read from the navigation, and a reload of the view loses it,
    // so it falls back to the movement every plant registers
    const form = getEntryAndExitFormByKey(state?.formKey);
    const formKey = form.key;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission, user } = useSelector((state) => state.authenticatioauthennSlice);
    // A guard carries no permissions, its user type opens the folios it works on at the gate
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const [emptyFields, setEmptyFields] = useState(false);
    const [emptyFieldsItems, setEmptyFieldsItems] = useState(false);
    const [materialRequest, setMaterialRequest] = useState(buildEntryAndExitForm(form.formModel));
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL });
    const { handleChange } = HandleChange({ model: materialRequest, setModel: setMaterialRequest });
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [watchers, setWatchers] = useState([]);
    const [responsibleDepartments, setResponsibleDepartments] = useState([]);

    /**
     * Handles the material request creation process.
     *
     * Validates:
     * - General form fields.
     * - Material request items.
     * - Selected employees, watchers and responsible departments.
     * - Uploaded evidence files.
     *
     * If validation fails:
     * - Displays validation warnings.
     *
     * If validation succeeds:
     * - Sends the request payload to the backend.
     * - Displays success feedback.
     * - Redirects the user back to the material request list.
     *
     * @returns {void}
     */
    const handleAction = () => {
        // The materials are marked on their own, because the folio is
        // registered with none of them and only the one that was started is the
        // one that has to be completed
        const validItems = materialRequest?.isValidItems();

        if (!materialRequest.isValid({ photoPicker, selectedEmployees, watchers, responsibleDepartments }) || !validItems) {
            setEmptyFields(true);
            setEmptyFieldsItems(!validItems);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        dispatch(CreateMaterialControl(materialRequest.toRegister({ selectedEmployees, watchers, responsibleDepartments, files: photoPicker.listFiles}))).then(({payload:{data:{success, code}}})=>{
            if (success){
                toast.success(t('has_been_created_successfully'));
                handleOnBack();
            }else errorMsg(code)
        });
    };

    /**
     * Navigates back to the material request main page.
     *
     * @returns {void}
     */
    const handleOnBack = () => {
        navigate(routes.materialControl.main);
    };

    /**
     * Resets the material request form to its initial state.
     *
     * Clears:
     * - General request information.
     * - Uploaded files.
     * - Selected images.
     * - Item list data.
     * - Selected employees, watchers and responsible departments.
     *
     * @returns {void}
     */
    const resetForm = () => {
        setMaterialRequest(buildEntryAndExitForm(form.formModel));
        setPhotoPicker({ ...PHOTO_PICKER_MODEL });
        setSelectedEmployees([]);
        setWatchers([]);
        setResponsibleDepartments([]);
    };

    return {
        t,
        DEV,
        isGuard,
        permission,
        navigate,
        emptyFields,
        emptyFieldsItems,
        setEmptyFieldsItems,
        materialRequest,
        setMaterialRequest,
        handleChange,
        photoPicker,
        setPhotoPicker,
        selectedEmployees,
        setSelectedEmployees,
        watchers,
        setWatchers,
        responsibleDepartments,
        setResponsibleDepartments,
        formKey,
        resetForm,
        handleOnBack,
        handleAction
    };
};

export default CreateEntryAndExitFormApp;
